import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getHostNoteEmbeddingStatus, Listing } from "src/api/";
import { RootState } from "src/redux/store";

export interface ListingsState {
  listings: Listing[];
  firstTime: boolean;
  selectedListingId: string;
  toggleInfo: {
    listings: string[];
    action: "enable" | "disable" | "enableAll" | "disableAll" | "";
  };
  pendingBestyResponses: string[];
  hostNoteStatePollerListingIds: string[];

  // show total number of host notes that have been processed and total number of host notes
  hostNotePollerStatuses: {
    [key: string]: {
      numProcessed: number;
      numTotal: number;
    };
  };
  userToken: string;
  // messy to include it here, but it's needed for the host note poller
}

const initialState: ListingsState = {
  listings: [],
  selectedListingId: "",
  toggleInfo: { listings: [], action: "" },
  firstTime: false,
  pendingBestyResponses: [],
  hostNoteStatePollerListingIds: [],
  hostNotePollerStatuses: {},
  userToken: "",
};

async function pollAllHostNoteStatuses(
  userToken: string,
  hostNoteStatePollerListingIds: string[],
  state: ListingsState
) {
  // This function polls the embedding status for all listings that are not yet embedded
}

export const pollHostNoteStatuses = createAsyncThunk(
  "listings/pollHostNoteStatuses",
  async ({ userToken, state }: { userToken: string; state: ListingsState }, { dispatch, getState }) => {
    if (!userToken) {
      return;
    }

    try {
      const { hostNoteStatePollerListingIds } = (getState() as RootState).listings;
      const listingIds = [...hostNoteStatePollerListingIds];

      for (const listingId of listingIds) {
        const data = await getHostNoteEmbeddingStatus({ userToken, listingId });

        dispatch(
          listingsSlice.actions.setHostNotePollerStatus({
            listingId,
            status: {
              numProcessed: data?.hostnotes_embedding_user_status?.i || 0,
              numTotal: data?.hostnotes_embedding_user_status?.n || 0,
            },
          })
        );

        // Only update embedded status if hostnotes ARE currently being processed
        // If hostnotes_embedding_status is null, it could mean either:
        // 1. Not started (listing not embedded)
        // 2. Completed (listing is embedded)
        // So we only set embedded=false when we know for sure hostnotes are being processed
        if (data?.hostnotes_embedding_status) {
          // Host notes are being processed, so not fully embedded yet
          dispatch(listingsSlice.actions.setEmbeddingFalse(listingId));
        } else {
          // Host notes are not being processed (either not started or completed)
          // Don't modify embedded status - let backend be source of truth
          dispatch(listingsSlice.actions.removeFromHostNoteStatePoller(listingId));
        }
      }

      await new Promise((resolve) => setTimeout(resolve, 5000));
    } catch (error) {
      console.error("Error polling host note statuses:", error);
    }
  }
);

const listingsSlice = createSlice({
  name: "listings",
  initialState,
  reducers: {
    setFirstTime: (state, action) => {
      state.firstTime = action.payload;
    },
    setListings: (state, action) => {
      state.listings = action.payload.listings;
      state.firstTime = action.payload.firstTime;
      state.selectedListingId = action.payload.listings.length > 0 ? action.payload.listings[0].id : "";
      const hostNoteStatePollerListingIds = state.listings
        .filter((listing) => !listing.embedded)
        .map((listing) => listing.id);
      state.hostNoteStatePollerListingIds = hostNoteStatePollerListingIds;
      if (action.payload.userToken) {
        state.userToken = action.payload.userToken;
      }

      // run in BG
      /*
      setTimeout(() => {
        pollAllHostNoteStatuses(action.payload.userToken, hostNoteStatePollerListingIds, state);
      }, 0);
      */
    },

    setSelectedListingId: (state, action) => {
      state.selectedListingId = action.payload;
    },
    enableAllListings: (state, action) => {
      state.listings = state.listings.map((listing) => {
        return {
          ...listing,
          enabled: true,
        };
      });
    },
    disableAllListings: (state, action) => {
      state.listings = state.listings.map((listing) => {
        return {
          ...listing,
          enabled: false,
        };
      });
    },
    toggleListing: (state, action) => {
      const listing = state.listings.find((listing) => listing.id === action.payload);
      if (listing) {
        listing.enabled = !listing.enabled;
      }
    },
    enableListing: (state, action) => {
      const listing = state.listings.find((listing) => listing.id === action.payload);
      if (listing) {
        listing.enabled = true;
      }
    },
    disableListing: (state, action) => {
      const listing = state.listings.find((listing) => listing.id === action.payload);
      if (listing) {
        listing.enabled = false;
      }
    },
    setEmbeddingFalse: (state, action) => {
      const listing = state.listings.find((listing) => listing.id === action.payload);
      if (listing) {
        listing.embedded = false;
      }
    },
    setEmbeddingTrue: (state, action) => {
      const listing = state.listings.find((listing) => listing.id === action.payload);
      if (listing) {
        listing.embedded = true;
      }
    },
    addToHostNoteStatePoller: (state, action) => {
      state.hostNoteStatePollerListingIds.push(action.payload);
    },
    removeFromHostNoteStatePoller: (state, action) => {
      state.hostNoteStatePollerListingIds = state.hostNoteStatePollerListingIds.filter((id) => id != action.payload);
    },
    setHostNotePollerStatus: (state, action) => {
      state.hostNotePollerStatuses[action.payload.listingId] = action.payload.status;
    },
    addMessage: (state, action) => {
      const listing = state.listings.find((listing) => listing.id === action.payload.listingId);
      listing?.thread.push(action.payload.message);
    },
    setListingsToToggle: (state, action) => {
      state.toggleInfo = action.payload;
    },
    addPendingBestyResponse: (state, action) => {
      state.pendingBestyResponses.push(action.payload);
    },
    removePendingBestyResponse: (state, action) => {
      state.pendingBestyResponses = state.pendingBestyResponses.filter((id) => id != action.payload);
    },
  },
});

export const {
  setListings,
  setSelectedListingId,
  disableAllListings,
  enableAllListings,
  toggleListing,
  setEmbeddingFalse,
  addMessage,
  setListingsToToggle,
  setFirstTime,
  addPendingBestyResponse,
  removePendingBestyResponse,
  enableListing,
  disableListing,
  addToHostNoteStatePoller,
  removeFromHostNoteStatePoller,
} = listingsSlice.actions;

export const selectListings = (state: RootState) => state.listings.listings;
export const getSelectedListingId = (state: RootState) => state.listings.selectedListingId;

export const getFirstTime = (state: RootState) => state.listings.firstTime;

export const getToggleInfo = (state: RootState) => state.listings.toggleInfo;

export const getSelectedListing = (state: RootState) =>
  state.listings.listings.find((listing) => listing.id === state.listings.selectedListingId) || {};

export const getPendingBestyResponses = (state: RootState) => state.listings.pendingBestyResponses;

export default listingsSlice.reducer;

export const getHostNotePollerStatuses = (state: RootState) => state.listings.hostNotePollerStatuses;
